/**
 * Custom collection sort by remaining stock (meters), not a native Shopify
 * sort_by value. Shopify's collection sort_by is a closed enum with no
 * inventory option, and the grid is server-paginated — sorting only the
 * current page would silently hide higher-stock items on other pages,
 * which is exactly what this feature exists to avoid for B2B buyers
 * hunting for large meterage.
 *
 * So: fetch every page of the collection via the Section Rendering API
 * (in parallel batches, preserving whatever filters are active), sort the
 * complete set of cards by `data-inventory`, and replace the grid. Those
 * fetches carry `sort_by=<value>-fetch` — the section Liquid widens
 * products_per_page to 250 for exactly that suffixed value (see
 * main-collection-product-grid.liquid), so the biggest collections need
 * ~7 requests instead of the ~130+ the normal per-page setting would take.
 *
 * The URL bar and the <select> itself use the plain, unsuffixed value
 * (see updateUrl()) so reload/shared-link restore still works — but a real
 * full-page navigation with that plain value must NOT get the widened page
 * size: 250 full product cards rendered directly into one page's HTML is
 * enough to crash the tab on a phone (confirmed in QA). The suffix keeps
 * "this is one of inventory-sort.js's own fetches" distinct from "this is
 * a real page load that happens to have our sort_by in its URL."
 */
(function () {
  const SORT_VALUES = { descending: 'inventory-descending', ascending: 'inventory-ascending' };
  const SORT_VALUE_SET = new Set(Object.values(SORT_VALUES));
  const BATCH_SIZE = 6;
  const REVEAL_CHUNK_SIZE = 40;

  function directionFromValue(value) {
    return value === SORT_VALUES.descending ? 'descending' : 'ascending';
  }

  class InventorySort {
    constructor() {
      this.controller = null;
      this.revealObserver = null;
      // The shopper's actual choice, tracked independently of the <select>
      // DOM — Shopify's own "remove filter" links (filter.url_to_remove,
      // results_url) are built server-side and don't carry our unrecognized
      // sort_by value, so the native re-render they trigger silently resets
      // the visible <select> to the collection's default sort. Re-deriving
      // "is inventory sort active" from that DOM state after the fact would
      // just inherit that reset; this flag is the source of truth instead.
      this.activeValue = null;
    }

    abort() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      // facet:updated calls this when the shopper has moved off this sort
      // (a filter change, a different sort option) without ever calling
      // run() again — render() would normally disconnect this, but if a
      // chunked reveal was still pending it never gets the chance, and the
      // observer's closure keeps every unrevealed card node alive forever.
      if (this.revealObserver) {
        this.revealObserver.disconnect();
        this.revealObserver = null;
      }
    }

    async run(value, sourceSelect) {
      this.abort();
      this.activeValue = value;

      const list = document.getElementById('ProductsList');
      const container = document.getElementById('ProductGridContainer');
      if (!list || !container) return;

      const controller = new AbortController();
      this.controller = controller;
      const direction = directionFromValue(value);

      this.setLoading(true);

      try {
        const sectionId = FoxTheme.utils.getSectionId(list);
        const baseUrl = new URL(window.location.href);
        // A "-fetch"-suffixed value, distinct from the plain one that sits in
        // the URL bar (see updateUrl()) — the section Liquid widens
        // products_per_page to 250 only for this exact suffixed value, never
        // for the plain one. These are internal fetches, not a real page
        // navigation, so it's safe here; a real full-page load/reload still
        // carries the plain value and renders at the normal page size. Mixing
        // the two up crashed the tab on a phone in QA: a real reload was
        // rendering 250 full product cards directly into the page's own HTML.
        baseUrl.searchParams.set('sort_by', `${value}-fetch`);
        baseUrl.searchParams.delete('page');
        baseUrl.searchParams.set('section_id', sectionId);

        const firstUrl = new URL(baseUrl);
        firstUrl.searchParams.set('page', '1');
        const firstDoc = await this.fetchDoc(firstUrl, controller.signal);

        const firstContainer = firstDoc.getElementById('ProductGridContainer');
        const totalPages = parseInt((firstContainer && firstContainer.dataset.totalPages) || '1', 10) || 1;

        let cards = this.extractCards(firstDoc);

        const remainingPages = [];
        for (let page = 2; page <= totalPages; page++) remainingPages.push(page);

        for (let i = 0; i < remainingPages.length; i += BATCH_SIZE) {
          const batch = remainingPages.slice(i, i + BATCH_SIZE);
          const docs = await Promise.all(
            batch.map((page) => {
              const url = new URL(baseUrl);
              url.searchParams.set('page', String(page));
              return this.fetchDoc(url, controller.signal);
            })
          );
          docs.forEach((doc) => {
            cards = cards.concat(this.extractCards(doc));
          });
        }

        const sign = direction === 'descending' ? -1 : 1;
        cards.sort((a, b) => sign * (a.inventory - b.inventory));

        // A newer run may have started (and gathered all its data) while this
        // one was still fetching. AbortError only catches fetches still
        // in-flight at that point — a run that had already moved past its
        // last fetch keeps sorting/rendering regardless, so without this
        // check it can win the race and paint stale results over the newer
        // run's. Only the run whose controller is still "current" may render.
        if (this.controller !== controller) return;

        this.render(list, container, cards.map((card) => card.node));
        this.syncSelects(value, sourceSelect);
        this.updateUrl(value);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[inventory-sort]', error);
        }
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.setLoading(false);
        }
      }
    }

    async fetchDoc(url, signal, attempt = 0) {
      let response;
      try {
        response = await fetch(url.toString(), { signal });
      } catch (error) {
        if (error.name === 'AbortError' || attempt >= 1) throw error;
        return this.fetchDoc(url, signal, attempt + 1);
      }

      if (!response.ok) {
        if (attempt < 1) return this.fetchDoc(url, signal, attempt + 1);
        // Out of retries and still failing: surface it. Silently parsing
        // an error page as if it were a product grid would just look like
        // that page's products vanished from the sort, with no indication
        // why.
        throw new Error(`inventory-sort: ${response.status} fetching ${url}`);
      }

      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    }

    extractCards(doc) {
      const nodes = doc.querySelectorAll('#ProductsList > .product-column[data-inventory]');
      return Array.from(nodes).map((node) => ({
        node,
        inventory: parseFloat(node.getAttribute('data-inventory')) || 0,
      }));
    }

    render(list, container, nodes) {
      if (this.revealObserver) {
        this.revealObserver.disconnect();
        this.revealObserver = null;
      }

      const pagination = container.querySelector('.pagination');
      if (pagination) pagination.remove();

      list.replaceChildren();
      this.revealChunked(list, nodes);
      document.dispatchEvent(new CustomEvent('collection:rerendered'));
    }

    revealChunked(list, nodes) {
      let index = 0;
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      // theme.css hides every empty div by default (`div:empty { display:
      // none }`) unless it opts out via `.no-empty` — without it the
      // sentinel never gets real geometry and the observer never fires.
      sentinel.className = 'inventory-sort-sentinel no-empty';
      sentinel.style.gridColumn = '1 / -1';
      sentinel.style.width = '100%';
      sentinel.style.height = '1px';

      const appendChunk = () => {
        sentinel.remove();
        nodes.slice(index, index + REVEAL_CHUNK_SIZE).forEach((node) => list.appendChild(node));
        index += REVEAL_CHUNK_SIZE;

        if (index < nodes.length) {
          list.appendChild(sentinel);
        } else if (this.revealObserver) {
          this.revealObserver.disconnect();
          this.revealObserver = null;
        }
      };

      appendChunk();

      if (index < nodes.length) {
        this.revealObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) appendChunk();
          },
          { rootMargin: '400px 0px 400px 0px' }
        );
        this.revealObserver.observe(sentinel);
      }
    }

    setLoading(isLoading) {
      document.querySelectorAll('[data-facet-loading]').forEach((el) => {
        el.classList.toggle('btn--loading', isLoading);
      });
      document.querySelectorAll('select[is="facet-short"]').forEach((el) => {
        el.disabled = isLoading;
      });
    }

    syncSelects(value, sourceSelect) {
      // Setting .value only — NOT dispatching a 'change' event. A capture-
      // phase listener can be told to ignore its own synthetic event, but
      // FacetShort's own listener is bound directly on the select for the
      // target phase, which fires regardless of what capture-phase listeners
      // do; that handler calls form.renderSection() directly (a real
      // facets.js fetch), which would silently overwrite this sorted grid
      // with a single default-order page moments later.
      document.querySelectorAll('select[is="facet-short"]').forEach((select) => {
        if (select === sourceSelect) return;
        const hasOption = Array.from(select.options).some((option) => option.value === value);
        if (!hasOption) return;
        select.value = value;
      });
    }

    updateUrl(value) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      url.searchParams.delete('page');
      history.replaceState({}, '', url.toString());
    }
  }

  const inventorySort = new InventorySort();

  // Capture phase: run before FacetShort/FacetForm's own bubbling 'change'
  // handlers see the event, so we can stop it from triggering their normal
  // single-page sort_by request for these two values.
  document.addEventListener(
    'change',
    (event) => {
      const select = event.target.closest('select[is="facet-short"]');
      if (!select) return;

      const value = select.value;
      if (!SORT_VALUE_SET.has(value)) {
        // The shopper explicitly picked a native sort — forget our own
        // tracked intent so a later filter change doesn't fight them by
        // silently switching back to inventory order. Also abort any run
        // still in flight: setLoading(true) disables every facet-short
        // select for the duration of a run, so a real shopper can't reach
        // this branch mid-fetch today — but that's an incidental side
        // effect of a different method, not a guarantee this listener
        // itself provides. Without this, a stale run that outlives it
        // would still finish and silently overwrite the native sort the
        // shopper just picked.
        inventorySort.activeValue = null;
        inventorySort.abort();
        return;
      }

      event.stopPropagation();
      inventorySort.run(value, select);
    },
    true
  );

  // Any filter/facet change re-renders the grid via the normal (single-page,
  // native-order) flow. If our sort is the active choice, redo it against
  // the new filter state so it doesn't silently fall back to page-1 order.
  // Deliberately reads inventorySort.activeValue, not the <select>'s current
  // DOM value: removing a filter uses a server-built link (url_to_remove /
  // results_url) that drops our unrecognized sort_by, so the native
  // re-render this triggers has already reset the visible <select> to the
  // collection's default sort by the time this fires.
  document.addEventListener('facet:updated', () => {
    inventorySort.abort();

    if (inventorySort.activeValue) {
      inventorySort.run(inventorySort.activeValue, null);
    }
  });

  // Reload / shared link with our sort_by still in the URL. This script is
  // deferred, so by the time it runs DOMContentLoaded has usually already
  // fired — a listener for it here would simply never call back — so check
  // readyState directly and only fall back to the event if we're somehow
  // still that early.
  function restoreFromUrl() {
    const value = new URL(window.location.href).searchParams.get('sort_by');
    if (value && SORT_VALUE_SET.has(value)) {
      inventorySort.run(value, null);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', restoreFromUrl);
  } else {
    restoreFromUrl();
  }
})();
